import BookingMessages from './BookingMessages.jsx';
// src/components/MyBookingsPanel.jsx
//
// Student "My Bookings" -- reads the user's /serviceRequests (Slice A) live and shows each
// request's lifecycle status. When 'quoted', renders the PaymentInstructionsBlock (PayMe/FPS
// + the server billReference + quoted amount) with "pay then await confirmation". Off-platform
// payment: there is no pay button -- the admin marks it paid after the transfer arrives.
import React from 'react';
import { useNavigate } from 'react-router-dom';
import PaymentInstructionsBlock from './PaymentInstructionsBlock.jsx';
import ServiceCardPayment from './ServiceCardPayment.jsx';
import { useMyServiceRequests } from '../storage/useServiceRequests.js';
import { RedeemBookingInvitation, BookingReschedule } from './BookingInvitation.jsx';

const STATUS = {
    confirmed: { cls: 'bg-green-500/20 text-green-300', label: 'Confirmed · complimentary' },
    requested: { cls: 'bg-amber-500/20 text-amber-300', label: 'Requested · awaiting quote' },
    quoted:    { cls: 'bg-midnight-accent/25 text-midnight-accent', label: 'Quote received · pay to confirm' },
    paid:      { cls: 'bg-green-500/20 text-green-300', label: 'Paid · awaiting your session' },
    completed: { cls: 'bg-green-600/20 text-green-300', label: 'Completed' },
    rejected:  { cls: 'bg-red-500/20 text-red-300', label: 'Declined' },
};
function money(cents) { return 'HK$' + (Number(cents || 0) / 100).toLocaleString('en-HK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmt(ts) {
    if (!ts) return '—';
    const d = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function MyBookingsPanel({ state }) {
    const navigate = useNavigate();
    const { requests, loading } = useMyServiceRequests();
    const bookings = requests.filter((r) => r.type === 'booking'); // shared collection (Slice B)

    return (
        <div className="max-w-3xl space-y-4">
            <div className="mb-2">
                <h2 className="font-display text-midnight-text text-2xl mb-1">My Bookings</h2>
                <p className="font-serif italic text-midnight-muted text-sm">1-on-1 and small group sessions you've requested.</p>
            </div>

            <RedeemBookingInvitation />
            {loading ? (
                <p className="font-serif italic text-midnight-muted">Loading…</p>
            ) : bookings.length === 0 ? (
                <div className="border border-midnight-accent/20 bg-midnight-page/60 p-8 text-center decorative-corner text-midnight-accent">
                    <p className="font-serif italic text-midnight-muted mb-4">No bookings yet — visit the Booking page to request a session.</p>
                    <button onClick={() => navigate('/booking')}
                        className="px-5 py-2.5 border border-midnight-accent text-midnight-accent font-display text-sm hover:bg-midnight-accent/10 transition-colors font-semibold">
                        Request a Session
                    </button>
                </div>
            ) : bookings.map((r) => {
                const p = r.payload || {};
                const badge = STATUS[r.status] || { cls: 'bg-midnight-muted/20 text-midnight-muted', label: r.status };
                const sessionLabel = p.sessionType === 'small-group' ? 'Small Group' : '1-on-1 Session';
                return (
                    <div key={r.id} className="border border-midnight-accent/20 bg-midnight-page/60 p-5 decorative-corner text-midnight-accent">
                        <div className="flex items-start justify-between gap-3 mb-3">
                            <div className="min-w-0">
                                <div className="font-display text-midnight-text text-sm font-semibold">{sessionLabel}</div>
                                <div className="font-serif italic text-midnight-muted text-xs">
                                    {p.preferredDate ? fmt(p.preferredDate) : '—'}{p.preferredTime ? ' · ' + p.preferredTime : ''}
                                    {r.billReference ? ' · Ref ' + r.billReference : ''}
                                </div>
                                {p.topic && <div className="font-serif text-midnight-muted text-xs mt-1 truncate">{p.topic}</div>}
                            </div>
                            <span className={`px-3 py-1.5 border-0 font-display text-sm whitespace-nowrap font-medium rounded-full ${badge.cls}`}>{badge.label}</span>
                        </div>

                        {r.status === 'quoted' && (
                            <div className="mt-3 border-t border-midnight-accent/15 pt-3 space-y-3">
                                <div className="font-serif text-midnight-text text-sm">
                                    Quoted total: <span className="font-display text-midnight-accent">{money(r.quotedAmount)}</span>
                                </div>
                                <ServiceCardPayment request={r} />
                                <details><summary className="cursor-pointer text-sm">Prefer PayMe / FPS?</summary><PaymentInstructionsBlock
                                    booking={state.bookingContent}
                                    referenceCode={r.billReference}
                                    prefilledMessage={`Booking payment ${money(r.quotedAmount)}, reference ${r.billReference}`}
                                    theme="midnight" />
                                <p className="font-serif italic text-midnight-muted text-xs">
                                    Pay using PayMe / FPS above, quoting reference <span className="text-midnight-accent">{r.billReference}</span>, then await confirmation. Danielle will mark this paid once your transfer arrives.
                                </p></details>
                            </div>
                        )}

                        {r.status === 'paid' && (
                            <p className="font-serif italic text-midnight-muted text-xs mt-2">Payment confirmed — your session is being arranged.</p>
                        )}
                        <BookingMessages request={r} />
                        {r.complimentary && r.status === 'confirmed' && <BookingReschedule request={r} />}
                        {r.status === 'rejected' && r.rejectReason && (
                            <p className="font-serif text-red-300 text-xs mt-2">Reason: {r.rejectReason}</p>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
